import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Star, Quote } from "lucide-react";

const TestimonialsSection = ({ testimonials }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0 }
  };

  // Sample testimonials if none exist
  const defaultTestimonials = [
    {
      id: "1",
      name: "Margaret Thompson",
      relationship: "Daughter of client",
      content: "Having someone there who understood the process and could guide us through those final days was invaluable. She brought such peace to our family during an incredibly difficult time. My mother passed surrounded by love, exactly as she wanted.",
      rating: 5
    },
    {
      id: "2",
      name: "Robert Chen",
      relationship: "Son of client",
      content: "I didn't know what to expect, but the compassionate care and support we received exceeded anything I could have imagined. She helped us focus on what truly mattered - being present with my father and creating beautiful final memories together.",
      rating: 5
    },
    {
      id: "3",
      name: "Sarah Williams",
      relationship: "Wife of client",
      content: "The guidance through the post-mortem process gave us the time we needed to say goodbye properly. It was handled with such dignity and respect. I will forever be grateful for the peace she brought to our family.",
      rating: 5
    }
  ];

  const displayTestimonials = testimonials?.length > 0 ? testimonials : defaultTestimonials;

  return (
    <section 
      id="testimonials" 
      className="relative py-24 md:py-32 bg-[#F9F8F4]"
      ref={ref}
      data-testid="testimonials-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={fadeInUp}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-sm uppercase tracking-widest text-[#8F9E82] font-bold mb-4">
            Words of Gratitude
          </p>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium text-[#2D2A26] mb-6">
            Testimonials
          </h2>
          <p className="text-lg text-[#5C5852] max-w-2xl mx-auto">
            Hear from families who have walked this journey with compassionate support.
          </p>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayTestimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
              variants={fadeInUp}
              transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
              className="bg-white rounded-2xl p-8 shadow-[0_4px_24px_rgba(74,93,35,0.06)] border border-[#4A5D23]/10 hover:border-[#4A5D23]/20 transition-all duration-300"
              data-testid={`testimonial-card-${index}`}
            >
              <Quote className="w-10 h-10 text-[#8F9E82]/30 mb-4" />
              
              <p className="text-[#5C5852] leading-relaxed mb-6 italic">
                "{testimonial.content}"
              </p>

              <div className="flex items-center gap-1 mb-4">
                {[...Array(testimonial.rating || 5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-[#4A5D23] text-[#4A5D23]" />
                ))}
              </div>

              <div className="border-t border-[#E8E4D9] pt-4">
                <p className="font-semibold text-[#2D2A26]">{testimonial.name}</p>
                <p className="text-sm text-[#8F9E82]">{testimonial.relationship}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={fadeInUp}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-12"
        >
          <p className="text-[#5C5852] italic">
            Every family's journey is unique. Let me help you create a peaceful transition.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
